import React, { useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { LeadConfigModel } from '../../../../shared/models/lead.model';
import { LeadUiModel } from '../../../../shared/ui-models/lead.ui-model';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

interface Option {
  value: string;
  label: string;
}

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());

const toOptions = (values: string[]): Option[] =>
  values.map((value: string) => ({ value, label: titleCase(value) }));

interface StatusModalProps {
  show: boolean;
  lead: LeadUiModel;
  config: LeadConfigModel;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}

/**
 * Which reasons to offer for this resolution on this lead.
 *
 * Scoped to the lead's funnel. The service publishes the full enums as well —
 * they are what it validates against, and an older lead may hold a reason its
 * vertical no longer offers — but a picker should only show what can apply
 * here. A reservations agent offered "owner already has a furnishing vendor"
 * eventually picks it, and a loss gets counted as something it was not.
 *
 * Falls back to the flat list so the form still works against a service that
 * predates the per-vertical lists.
 */
export function reasonsFor(
  config: LeadConfigModel,
  vertical: string,
  reasonField?: string
): string[] {
  if (reasonField === 'notQualifiedReason') {
    return config.notQualifiedReasonsByVertical?.[vertical] ?? config.notQualifiedReasons ?? [];
  }
  if (reasonField === 'notInterestedReason') {
    return config.notInterestedReasonsByVertical?.[vertical] ?? config.notInterestedReasons ?? [];
  }
  return [];
}

export const StatusModal: React.FC<StatusModalProps> = ({
  show,
  lead,
  config,
  onClose,
  onSubmit
}) => {
  const [status, setStatus] = useState('pending');
  const [resolutionType, setResolutionType] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setStatus('pending');
    setResolutionType('');
    setReason('');
    setNote('');
  }, [show]);

  /**
   * Only the selectable list. The full `resolutionTypesByVertical` includes
   * values the service sets itself — `unreachable`, set when the attempt quota
   * runs out — and sending one back is rejected outright. Offering it would be
   * a picker whose options cannot all be picked.
   */
  const resolutionOptions = toOptions(
    config.selectableResolutionTypesByVertical[lead.vertical] ?? []
  );

  // Which reason field this resolution needs, straight from the config so the
  // rule lives in one place rather than being re-encoded here.
  const reasonField = config.requiredReasonByResolution[resolutionType];
  const reasonOptions = toOptions(reasonsFor(config, lead.vertical, reasonField));

  const isResolving = status === 'resolved';
  const needsPendingReason = status === 'pending';
  const canSubmit =
    (!isResolving || (resolutionType.length > 0 && (!reasonField || reason.length > 0))) &&
    (!needsPendingReason || reason.length > 0);

  // `duplicate` has its own action because it needs the id of the lead it
  // duplicates; `not_reachable` is set by the service, never by a person.
  const statusOptions = toOptions(
    config.statuses.filter((option: string) => option !== 'duplicate' && option !== 'not_reachable')
  );

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = { status };
    if (isResolving) {
      payload.resolutionType = resolutionType;
      if (reasonField) payload[reasonField] = reason;
      if (note.trim()) payload.resolutionNote = note.trim();
    }
    if (needsPendingReason) payload.pendingReason = reason;
    return payload;
  };

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[85vh] lg:w-[520px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Change status
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={!canSubmit}
            tracking={{
              event: 'record_saved',
              entity: 'lead',
              action: 'update',
              outcome: 'success'
            }}
            onClick={() => onSubmit(buildPayload())}
          >
            Save
          </Button>
        </div>
      }
    >
      {/* `w-full` is load-bearing: ui/Model's body is `flex flex-col
          items-start`, so a child that does not claim the width shrinks to its
          content — which collapsed every react-select in here to about 85px.
          Every other user of this modal wraps its children the same way. */}
      <div className="flex w-full flex-col gap-4">
        <div>
          <label className={LABEL}>Status</label>
          <Select
            menuPortalTarget={document.body}
            isSearchable={false}
            menuPlacement="auto"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={statusOptions}
            value={statusOptions.find((option: Option) => option.value === status) ?? null}
            onChange={(option: ValueType<Option, false>) => {
              setStatus(option ? (option as Option).value : 'pending');
              setReason('');
              setResolutionType('');
            }}
          />
        </div>

        {isResolving ? (
          <div>
            <label className={LABEL}>Resolution</label>
            <Select
              menuPortalTarget={document.body}
              isSearchable={false}
              menuPlacement="auto"
              styles={selectMenuPortalStyles}
              classNamePrefix="react-select"
              options={resolutionOptions}
              placeholder="Select a resolution"
              value={
                resolutionOptions.find((option: Option) => option.value === resolutionType) ?? null
              }
              onChange={(option: ValueType<Option, false>) => {
                setResolutionType(option ? (option as Option).value : '');
                setReason('');
              }}
            />
          </div>
        ) : null}

        {(reasonField && isResolving) || needsPendingReason ? (
          <div>
            <label className={LABEL}>
              {needsPendingReason ? 'Why is it pending?' : titleCase(reasonField ?? 'Reason')}
            </label>
            <Select
              menuPortalTarget={document.body}
              isSearchable={false}
              menuPlacement="auto"
              styles={selectMenuPortalStyles}
              classNamePrefix="react-select"
              options={needsPendingReason ? toOptions(config.pendingReasons) : reasonOptions}
              placeholder="Select a reason"
              value={
                (needsPendingReason ? toOptions(config.pendingReasons) : reasonOptions).find(
                  (option: Option) => option.value === reason
                ) ?? null
              }
              onChange={(option: ValueType<Option, false>) =>
                setReason(option ? (option as Option).value : '')
              }
            />
          </div>
        ) : null}

        {isResolving ? (
          <div>
            <label className={LABEL}>Note (optional)</label>
            <textarea
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
